import React, { useState, useEffect, useRef } from "react";

import { Link } from "gatsby";
import { GatsbyImage } from "gatsby-plugin-image";

import AddToCartButton from "./addToCartButton";
import ProductAddToCart from "./productAddToCart";

import Container from "../shared/container";
import DarkBlueButton from "../shared/darkBlueButton";
import PrimaryButton from "../shared/primaryButton";
import QuantitySelector from "../shared/quantitySelector";

import { __t } from "../../utils/translate";
import { Product } from "../../utils/types";
import { sendEvent } from "../../utils/api";
import { getGatsbyImageData, gtag } from "../../utils/utils";
import { useStore } from "@nanostores/react";
import { $couponHeight } from "@/utils/store";
import { useOptions } from "@/hooks/useOptions";

import { useProductContext } from "@/providers/productProvider";
import Checkbox from "../shared/checkbox";

type CouponProps = {
    product: Product;
    className?: string;
    attributesBundle: { products: Record<string, unknown[]>; };
    setAttributeBundle: (attribute: any) => void;
    prices: Record<string, number>;
    handleScroll: () => void;
};

const Coupon: React.FC<CouponProps> = ({
    className = "",
    product,
    attributesBundle,
    setAttributeBundle,
    prices,
    handleScroll
}) => {
    const { quantity, setQuantity, attributes, variationId, image } = useProductContext();

    const imageData = getGatsbyImageData(image.localFile);
    const alt = image.alt;

    const options = useOptions();
    const couponHTML = options.wpOptions.data.description_email_completed;

    const couponRef = useRef(null);
    const couponHeight = useStore($couponHeight);

    const [isVisible, setIsVisible] = useState(false);
    const [isClosed, setIsClosed] = useState(false);
    const [isCouponVisible, setIsCouponVisible] = useState(false);
    const [email, setEmail] = useState('');
    const [emailError, setEmailError] = useState<string | null>(null);
    const [isChecked, setIsChecked] = useState(true);

    const border_color = emailError ? 'border-error' : 'border-stroke-color';
    const text_color = emailError ? 'placeholder:text-error' : 'placeholder:text-body-text-color';

    const gotAttributes = Object.keys(attributes).length > 0 || Object.keys(attributesBundle).length > 1;

    useEffect(() => {
        if (typeof window === "undefined") return;

        const handleScroll = () => {
            setIsVisible(window.scrollY > 800);
        };

        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    useEffect(() => {
        if (isVisible && (couponHeight !== couponRef.current.clientHeight)) {
            $couponHeight.set(couponRef.current.clientHeight);
        }

        return () => {
            $couponHeight.set(0);
        };

    }, [isVisible, isCouponVisible, isClosed]);

    const handleClose = () => {
        setIsClosed(true);
    };

    const getCoupon = async () => {
        if (
            !email
                .toLowerCase()
                .match(
                    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
                )
        ) {
            setEmailError("Please provide a valid email address.");
            return;
        }

        setIsCouponVisible(true);
        localStorage.setItem('couponVisible', 'true');
        localStorage.setItem('billing_email', email);

        gtag("event", "get_coupon");
        if (typeof window !== "undefined") {
            const res = await sendEvent('mailgen', { email: email, page: window.location.pathname });
        }

        setEmailError(null);
    };

    const handleAddToCart = () => {
        if (attributes && Object.keys(attributes).length > 0) {
            handleScroll();
        } else {
            console.log(attributes);
        }
    }

    return (
        <>
            <div ref={couponRef} className={`${className} ${isVisible ? 'max-h-[500px]' : 'max-h-0'}  h-fit fixed bottom-0 left-0 bg-light-gray w-full z-30`}>
                <Container>

                    {process.env.GATSBY_COUPON_20 === 'true' && !isClosed && (
                        <div className="w-full pt-1 lg:pt-0
                                sm:pt-0
                                md:pr-11 
                                lg:pr-3
                                xl:pr-14
                                xl:gap-[140px] relative">
                            {!isCouponVisible && (
                                <div className="flex flex-col justify-between lg:items-center gap-2 md:flex-row md:items-center md:gap-3">
                                    <p className="text-18 xl:text-h3 lg:mt-4 lg:mb-4 pl-3 text-center lg:text-start text-dark md:w-1/2 xl:pl-[68px] xl:mt-[15px] xl:mb-5">
                                        {__t("Up to 20% OFF Your First Purchase")}
                                    </p>
                                    <button aria-label="coupon close" onClick={handleClose} className='lg:block hidden underline text-18 xl:text-h3 whitespace-nowrap mt-[17px] lg:mt-0'>
                                        {__t("Decline Offer")}
                                    </button>
                                    <div className="flex flex-col md:w-[355px] lg:w-[520px] xl:w-[500px]">
                                        <div className="flex flex-row w-full md:flex-none xl:flex-auto flex-nowrap sm:pt-[12px]">
                                            <div className="grow bg-transparent mr-2 xl:mr-14">
                                                <div className="flex flex-col gap-1">
                                                    <input
                                                        className={`${border_color} ${text_color} h-[44px] py-2 pl-3 w-full text-base-16 border bg-white text-dark placeholder:text-gray focus:placeholder:opacity-0`}
                                                        type="email"
                                                        id="email-input"
                                                        value={email}
                                                        onChange={(e) => setEmail(e.target.value)}
                                                        placeholder={__t("Email")}
                                                    />
                                                    <div className={`${emailError ? 'h-5 mt-2 opacity-100 visible' : 'h-0 opacity-0 invisible'}
                            text-error text-small-14 transition-all ease-in-out duration-1000`}
                                                    >
                                                        {emailError}
                                                    </div>
                                                </div>
                                            </div>
                                            <DarkBlueButton
                                                onClick={getCoupon}
                                                className="h-[44px] bg-button-accent-color border-2 border-button-accent-color px-3 sm:px-5"
                                                textStyle="text-white text-small-14 font-semibold"
                                                isDisabled={!isChecked}
                                            >
                                                {__t("Get Coupon")}
                                            </DarkBlueButton>
                                        </div>

                                        <div className="flex flex-row items-center justify-center lg:justify-start lg:max-h-[20px] text-[12px] xl:text-[15px] leading-3">
                                            <Checkbox
                                                id="newsletter"
                                                name="newsletter"
                                                label={__t(" I want to subscribe to the newsletter. ")}
                                                className="gap-1"
                                                defaultState={isChecked}
                                                onClick={() => setIsChecked(prev => !prev)}
                                                isInline={true}
                                                width="16"
                                                height="16"
                                            />
                                            <Link className="underline ml-1 text-dark-blu" to="/privacy-policy" target="_blank">{__t("View terms")}</Link>
                                        </div>
                                    </div>
                                </div>
                            ) || (
                                    <div className="flex w-full pt-3 sm:p-[12px] bg-light-gray pb-[4px]">
                                        <div
                                            className="coupon-codes mx-auto text-center text-small-14 md:text-base-16 lg:text-18 lg:leading-6 text-dark"
                                            dangerouslySetInnerHTML={{ __html: couponHTML }}
                                        />
                                    </div>
                                )}

                            <button aria-label="coupon close" onClick={handleClose} className={`${!isCouponVisible ? 'lg:hidden' : ''} absolute top-2 right-0 w-3 h-3 md:w-5 md:h-5 md:top-6 lg:right-6 xl:right-16`}>
                                <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" version="1.1">
                                    <path fill="#5A5A5A" stroke="#222222" strokeWidth="4" d="M 20,4 3,21 33,50 3,80 20,97 49,67 79,97 95,80 65,50 95,20 80,4 50,34 z" />
                                </svg>
                            </button>
                        </div>
                    )}

                    <div className="max-w-[1190px] mx-auto w-full px-3 lg:flex flex-row justify-between items-center">
                        <div className="hidden lg:flex lg:w-full flex-row items-center md:pb-1 lg:pb-0">
                            <GatsbyImage
                                loading="lazy"
                                image={imageData}
                                alt={alt}
                                className={` w-full md:w-[65px] border border-stroke-color mr-2`}
                                sizes="(max-width: 65px) 65px, 65px"
                            />
                            <p className='text-base-16 text-dark'>{product.name}</p>
                        </div>


                        <div className={`${product.stock_status === 'instock' ? '' : 'hidden'}
                                        justify-center lg:justify-end
                                        flex flex-row gap-3 sm:gap-4 items-center w-full py-1 sm:py-2`}>
                            {gotAttributes && (
                                <PrimaryButton
                                    className={`w-fit py-2 px-5 rounded-sm`}
                                    textStyle="!text-small-14 font-semibold"
                                    onClick={handleAddToCart}>
                                    {__t("Add to cart")}
                                </PrimaryButton>
                            ) || (
                                    <>
                                        <AddToCartButton
                                            className={`h-[40px] !px-[10px] sm:!px-5 z-50 rounded-sm`}
                                            textStyle="!text-small-14 !font-bold"
                                            product={product}
                                            variationId={variationId}
                                            productName={product.name}
                                            productPrice={parseInt(product.price)}
                                            quantity={quantity}
                                            bundle={attributesBundle}
                                            buttonText="Buy Now"
                                            isCoupon={true}
                                        />
                                        <QuantitySelector
                                            className="px-3 w-[35px] h-10 border-dark border-l-2 border-y-2"
                                            buttonStyle="right-[6px] top-0 !border-dark pr-[6px]"
                                            quantity={quantity}
                                            setQuantity={setQuantity}
                                            symbolHeight='20'
                                        />
                                        <AddToCartButton
                                            className={`h-[40px] !px-3 sm:!px-5 z-50 rounded-sm`}
                                            textStyle="!text-small-14 !font-bold"
                                            product={product}
                                            variationId={variationId}
                                            productName={product.name}
                                            productPrice={parseInt(product.price)}
                                            quantity={quantity}
                                            bundle={attributesBundle}
                                            isCoupon={true}
                                        />
                                    </>
                                )}
                        </div>
                    </div>
                </Container>
            </div>
        </>
    );
};

export default Coupon;
